// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useMemo, useRef } from 'react'
import styles from './OwnerNotesReader.module.css'
import OwnerHeroAsk from './OwnerHeroAsk'
import OwnerNoteSlides from './OwnerNoteSlides'
import { useOwnerViewBeacon } from 'lib/owner-notes/analytics'
import { OWNERS_ANCHOR, READER_ASK, READER_ASK_GATED } from 'lib/owner-notes/band-copy'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import { questionsFor } from 'lib/owner-notes/owner-figures'
import type { OwnersEnrichment } from 'lib/dealhunt/types'

/** Notes the reader pages through: the category card's three (#118) — the wall has the rest. */
export const OWNER_READER_NOTES = 3

interface OwnerNotesReaderProps {
  nodeId: string
  productName: string
  /** This product page — the writer handoff's product (`OwnerHeroAsk`). */
  productHref?: string | null
  owners: OwnersEnrichment | null | undefined
}

/**
 * "In their words" (design "4b Rail keeps the figures, the gap holds the notes", 2026-09-22):
 * the space under the gallery is the note reader — up to three notes at a comfortable reading
 * width, the pager beside the title, the ask attached. Owner evidence stays beside the buy
 * button (the rail card); owner voice fills the dead space. Nothing without a note: the rail
 * card carries the first-owner ask then. A slide half on screen for a second is a read of that
 * note (#138), the same beacon as the category card's carousel.
 */
const OwnerNotesReader: React.VFC<OwnerNotesReaderProps> = ({
  nodeId,
  productName,
  owners,
  productHref = null,
}) => {
  const aggregate = owners?.aggregate ?? null
  const notes = useMemo(() => (owners?.notes ?? []).slice(0, OWNER_READER_NOTES), [owners])
  const questions = useMemo(() => questionsFor(aggregate), [aggregate])
  const rootRef = useRef<HTMLElement>(null)
  useOwnerViewBeacon(rootRef, { enabled: notes.length > 0, deps: [notes.length] })
  // `OwnerHeroAsk`'s own handoff (same arguments): null-or-not is build flag + product slug only.
  const handoff = useWriterHandoff('rail', productHref, { hash: OWNERS_ANCHOR })

  if (notes.length === 0) {
    return null
  }
  // The ask opens as `rail`: its notice follows where that ask opens (build flag only).
  const ask = (handoff ? signInGateFor('community') : signInGateFor('product'))
    ? READER_ASK_GATED
    : READER_ASK
  return (
    <section
      className={styles.reader}
      ref={rootRef}
      aria-label={`What owners of the ${productName} say`}
    >
      <OwnerNoteSlides notes={notes} questions={questions} title="In their words" />
      <OwnerHeroAsk
        nodeId={nodeId}
        productName={productName}
        title={ask.title}
        text={ask.text}
        location="rail"
        productHref={productHref}
      />
    </section>
  )
}

export default OwnerNotesReader
